import { useCallback } from 'react';
import { useAppSelector } from 'src/utils/Hooks';
import AdvancedSearchRequestBuilder, {
  useCreateAdvancedSearchRequestBuilder
} from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import {
  UseAggregationBuilderWithMetricsArgs,
  useCreateAggregationBuilderWithMetrics
} from './useAggregationBuilderWithMetrics';
import { getSearchAppName, shouldShowNewAtlas } from 'src/utils/app';
import { useBeaconFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBeaconFilters';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import {
  buildBUEntityConditions,
  buildBusinessUnitConditionsV2
} from 'src/components/EntityPage/Filters/businessUnitFilters';
import { removeRetailPricesForBusinessUnits } from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/utils';
import {
  useAllSegmentsAndSearchTermLists,
  useAreCategoriesLoaded,
  useBrandsFollowingIds,
  useCategoryIds
} from 'src/utils/Hooks/reduxSelectors';

export interface UseBaseMetricRequestBuilderProps extends UseAggregationBuilderWithMetricsArgs {
  requestId: string;
  startWeekId?: number;
  endWeekId?: number;
  sortFieldName?: string;
  overrideDependentFields?: boolean;
  buildAggregationBuilder?: (aggregationBuilder: AggregationBuilder) => AggregationBuilder;
}

/**
 * Factory for creating a function to remove price range filters.
 * Can be added to any advanced search request with the `.apply` method
 */
export const useCreateRemoveRetailPriceRangeFilters = () => {
  return useCallback((indexName?: string) => {
    return (builder: AdvancedSearchRequestBuilder) => {
      if (
        !indexName ||
        [
          'new-content-metrics',
          'content',
          'advertising',
          'reviews',
          'review-metrics',
          'ratings-reviews-metrics-v2',
          'adjusted-forecast',
          'unadjusted-forecast',
          'buybox',
          'advertisingDisplay',
          'chargebacks',
          'aggregated-traffic',
          'recommendations-product'
        ].includes(indexName)
      ) {
        builder.apply((request) => {
          return request.removeConditionRangeFilters((rangeFilter) => rangeFilter.fieldName === 'retailPrice');
        });
      }
      return builder;
    };
  }, []);
};

export const removeDspClicks = (builder: AdvancedSearchRequestBuilder, shouldRemove = true) => {
  if (!shouldRemove) {
    return builder;
  }
  return builder.addConditionTermFilter('searchTerm', 'must_not', ['[DSP Traffic]']);
};

/**
 * Segments can include a filter for retail price. We want to filter
 * this condition out for certain indices because it will cause them
 * to return nothing.
 */
export const useRemoveRetailPriceRangeFilters = (indexName: string) => {
  const createRemoveRetailPriceRangeFilters = useCreateRemoveRetailPriceRangeFilters();
  return createRemoveRetailPriceRangeFilters(indexName);
};

/**
 * Returns a function that applies filters depending on the entity type.
 */
export const useApplyEntityFilters = () => {
  const entityType = useAppSelector((state) => state?.entityService?.mainEntity?.type);
  const retailer = useAppSelector((state) => state.retailer);
  const entityId = useAppSelector((state) => state?.entityService?.mainEntity?.id);
  const brandIds = useBrandsFollowingIds();
  const categoryIds = useCategoryIds();

  const { hasActiveFilters, appFilters } = useBeaconFilters();

  const segments = useAllSegmentsAndSearchTermLists();

  const { businessUnits, savedSearchesById } = useAppSelector((state) => state.segments);

  return useCallback(
    (requestBuilder: AdvancedSearchRequestBuilder) => {
      // For segments/searchtermlist, skip adding initial categoryIds because:
      // 1. The segment's own conditions already include categoryId (from parseSavedSearches)
      // 2. Those conditions will replace any filter we add here
      // 3. This avoids race conditions where categories aren't loaded yet
      const isSegmentOrSearchTermList = entityType === 'segment' || entityType === 'searchtermlist';

      // Only add categoryId filter if we have valid category IDs and not a segment
      // (segments use their own categoryId from their conditions)
      if (!isSegmentOrSearchTermList && categoryIds && categoryIds.length > 0) {
        requestBuilder.addConditionTermFilter('categoryId', 'should', categoryIds);
      }

      // Every request in Atlas should have the brandFollowing. - wanted to make it as not product, but it is widely used, so felt like it is worth to be here.
      if (shouldShowNewAtlas()) {
        requestBuilder.addConditionTermFilter('brandId', 'should', brandIds);
      }

      if (entityType === 'product') {
        requestBuilder.addConditionTermFilter('stacklineSku', 'should', [entityId]);
        // For Atlas, remove brandId filter for product entity (aligned with useAtlasV2ApplyEntityFilters)
        if (shouldShowNewAtlas()) {
          requestBuilder.removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');
        }
      } else if (entityType === 'brand') {
        // For Atlas, remove brandId filter first, then add back with entityId (aligned with useAtlasV2ApplyEntityFilters)
        if (shouldShowNewAtlas()) {
          requestBuilder.removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');
        }
        requestBuilder.addConditionTermFilter('brandId', 'should', [entityId]);
      } else if (entityType === 'category') {
        requestBuilder.addConditionTermFilter('categoryId', 'should', [entityId]);
        // For Atlas, remove brandId filter for category entity (aligned with useAtlasV2ApplyEntityFilters)
        if (shouldShowNewAtlas()) {
          requestBuilder.removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');
        }
      } else if (entityType === 'subcategory') {
        requestBuilder.addConditionTermFilter('subCategoryId', 'should', [entityId]);
        // For Atlas, remove brandId filter for subcategory entity (aligned with useAtlasV2ApplyEntityFilters)
        if (shouldShowNewAtlas()) {
          requestBuilder.removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');
        }
      } else if (entityType === 'segment' || entityType === 'searchtermlist') {
        const segment = segments.find((seg) => seg.id === entityId);
        if (segment) {
          // For Atlas, remove brandId filter since the segment's own conditions take precedence
          // (aligned with useAtlasV2ApplyEntityFilters)
          if (shouldShowNewAtlas()) {
            requestBuilder.removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');
          }
          if (segment.conditions.rangeFilters) {
            segment.conditions.rangeFilters.forEach((rangeFilter) => {
              requestBuilder.replaceConditionRangeFilter(
                rangeFilter.fieldName,
                rangeFilter.minValue,
                rangeFilter.maxValue
              );
            });
          }
          if (segment.conditions.termFilters) {
            segment.conditions.termFilters.forEach((termFilter) => {
              requestBuilder.replaceConditionTermFilter(termFilter.fieldName, termFilter.condition, termFilter.values);
            });
          }
        }
      } else if (entityType === 'businessunit') {
        const selectedBusinessUnit = businessUnits.find((businessUnit) => businessUnit.id === entityId);
        const nestedFilterConditions = buildBUEntityConditions(savedSearchesById, selectedBusinessUnit);

        if (selectedBusinessUnit) {
          // For Atlas, remove brandId filter for businessunit entity (aligned with useAtlasV2ApplyEntityFilters)
          if (shouldShowNewAtlas()) {
            requestBuilder.removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');
          }
          requestBuilder.addPreCalculatedNestedFilterCondition(
            removeRetailPricesForBusinessUnits(nestedFilterConditions)
          );
        }
      } else if (entityType === 'adEntity') {
        requestBuilder.addConditionTermFilter('entityId', 'should', [entityId]);
      } else if (entityType === 'adPortfolio') {
        requestBuilder.addConditionTermFilter('portfolioId', 'should', [entityId]);
      } else if (entityType === 'adCampaign') {
        requestBuilder.addConditionTermFilter('campaignId', 'should', [entityId]);
      }

      if (hasActiveFilters) {
        const { categoryFilters, subcategoryFilters, segmentFilters, businessUnitFilters } = appFilters;

        if (categoryFilters) {
          requestBuilder.replaceConditionTermFilter('categoryId', 'should', categoryFilters);
        }
        if (subcategoryFilters) {
          requestBuilder.replaceConditionTermFilter('subCategoryId', 'should', subcategoryFilters);
        }
        if (segmentFilters) {
          // TODO update segment filters to use range filters too, @Jonny
          const { termFilters } = segmentFilters;
          termFilters.forEach(({ fieldName, condition, values }) =>
            requestBuilder.replaceConditionTermFilter(fieldName, condition, values)
          );
        }

        if (businessUnitFilters) {
          const selectedBusinessUnits =
            entityType === 'businessunit'
              ? new Set([entityId as string, ...businessUnitFilters])
              : new Set(businessUnitFilters);

          const nestedFilterConditions = buildBusinessUnitConditionsV2(
            businessUnits,
            savedSearchesById!,
            retailer,
            selectedBusinessUnits
          );

          requestBuilder.addPreCalculatedNestedFilterCondition(
            removeRetailPricesForBusinessUnits(nestedFilterConditions)
          );
        }
      }

      return requestBuilder;
    },
    [
      appFilters,
      brandIds,
      businessUnits,
      categoryIds,
      entityId,
      entityType,
      hasActiveFilters,
      retailer,
      savedSearchesById,
      segments
    ]
  );
};

/**
 * Returns a factory function for getting a new request builder.
 */
export function useCreateBaseMetricRequestBuilder() {
  const applyEntityFilters = useApplyEntityFilters();

  const createRemoveRetailPriceRangeFilters = useCreateRemoveRetailPriceRangeFilters();
  const createAdvancedSearchRequestBuilder = useCreateAdvancedSearchRequestBuilder();
  const createAggregationBuilderWithMetrics = useCreateAggregationBuilderWithMetrics();

  return useCallback(
    ({
      requestId,
      startWeekId,
      endWeekId,
      indexName,
      fields,
      groupByFieldName,
      sortFieldName,
      buildAggregationBuilder,
      overrideDependentFields
    }: UseBaseMetricRequestBuilderProps) => {
      const requestBuilder = createAdvancedSearchRequestBuilder(requestId, `${getSearchAppName()}-${indexName}`);
      const aggregationBuilder = createAggregationBuilderWithMetrics({
        indexName,
        fields,
        groupByFieldName,
        overrideDependentFields
      });

      if (startWeekId) {
        aggregationBuilder.addConditionRangeFilter('weekId', startWeekId, endWeekId);
      }

      if (endWeekId && !startWeekId) {
        aggregationBuilder.addConditionRangeFilter('weekId', undefined, endWeekId);
      }

      const sortField = sortFieldName ? INDEX_FIELDS.getField(getSearchAppName(), indexName, sortFieldName) : undefined;
      if (sortField) {
        aggregationBuilder.setSortByAggregationField(
          sortField.displayName,
          sortField.name,
          sortField.aggregationFunction,
          true
        );
      }

      const aggBuilderToUse = buildAggregationBuilder
        ? buildAggregationBuilder(aggregationBuilder)
        : aggregationBuilder;

      requestBuilder
        .setPageNumber(1)
        .setPageSize(1200)
        .setPeriod('year')
        .setDoAggregation(true)
        .setReturnDocuments(false)
        .addAggregation(aggBuilderToUse.build())
        .setSearchBy('parent');

      if (startWeekId) {
        requestBuilder.addConditionRangeFilter('weekId', startWeekId, endWeekId);
      }

      if (endWeekId && !startWeekId) {
        requestBuilder.addConditionRangeFilter('weekId', undefined, endWeekId);
      }

      if (sortField) {
        requestBuilder.addSortField(sortField.displayName, sortField.name, sortField.aggregationFunction, true);
      }

      requestBuilder.apply(applyEntityFilters).apply(createRemoveRetailPriceRangeFilters(indexName));

      return requestBuilder;
    },
    [
      applyEntityFilters,
      createAdvancedSearchRequestBuilder,
      createAggregationBuilderWithMetrics,
      createRemoveRetailPriceRangeFilters
    ]
  );
}

/**
 * Creates the base request builder for a given
 * list of metric fields.
 *
 * Should be used multiple times when there are metrics with different indices.
 * For example, 'sales -> unitsSold' and 'traffic -> totalClicks' are under
 * two different APIs, so they should be requested separately.
 */
export const useBaseMetricRequestBuilder = (args: UseBaseMetricRequestBuilderProps): AdvancedSearchRequestBuilder => {
  const createBaseMetricRequestBuilder = useCreateBaseMetricRequestBuilder();
  return createBaseMetricRequestBuilder(args);
};
